import { useState } from 'react'
import { Plus, Pencil, Trash2, Monitor, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { usePlatforms, useCreatePlatform, useUpdatePlatform, useDeletePlatform } from '../api/useTestPlans'
import { useProjects } from '@/features/projects/api/useProjects'
import type { Platform, CreatePlatformRequest, UpdatePlatformRequest } from '@/types/testplan.types'
import { logger } from '@/lib/utils/logger'
import { formatDate } from '@/lib/utils'

export default function PlatformsPage() {
  const { data: platforms, isLoading, error } = usePlatforms()
  const { data: projects } = useProjects()
  const createMutation = useCreatePlatform()
  const updateMutation = useUpdatePlatform()
  const deleteMutation = useDeletePlatform()

  const [filterProjectId, setFilterProjectId] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create')
  const [editingPlatform, setEditingPlatform] = useState<Platform | null>(null)
  const [formName, setFormName] = useState('')
  const [formProjectId, setFormProjectId] = useState('')
  const [formNotes, setFormNotes] = useState('')
  const [formDesign, setFormDesign] = useState(true)
  const [formExecution, setFormExecution] = useState(true)

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [platformToDelete, setPlatformToDelete] = useState<{ id: number; name: string } | null>(null)

  const filteredPlatforms = (platforms || []).filter((p) => {
    if (filterProjectId !== 'all' && p.test_project_id.toString() !== filterProjectId) return false
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      return p.name.toLowerCase().includes(q) || p.notes?.toLowerCase().includes(q)
    }
    return true
  })

  const handleCreate = () => {
    setDialogMode('create')
    setEditingPlatform(null)
    setFormName(''); setFormProjectId(''); setFormNotes('')
    setFormDesign(true); setFormExecution(true)
    setDialogOpen(true)
  }

  const handleEdit = (platform: Platform) => {
    setDialogMode('edit')
    setEditingPlatform(platform)
    setFormName(platform.name)
    setFormProjectId(platform.test_project_id.toString())
    setFormNotes(platform.notes || '')
    setFormDesign(platform.enable_on_design)
    setFormExecution(platform.enable_on_execution)
    setDialogOpen(true)
  }

  // openapi: POST /api/platforms → 201
  const handleSubmit = async () => {
    if (!formName.trim()) { toast.error('Platform name is required'); return }
    try {
      if (dialogMode === 'create') {
        if (!formProjectId) { toast.error('Select a project'); return }
        const req: CreatePlatformRequest = {
          test_project_id: parseInt(formProjectId, 10),
          name: formName.trim(),
          notes: formNotes.trim() || undefined,
          enable_on_design: formDesign,
          enable_on_execution: formExecution,
        }
        await createMutation.mutateAsync(req)
        toast.success(`Platform "${formName}" created`)
      } else if (editingPlatform) {
        // openapi: PUT /api/platforms/{platform_id}
        const req: UpdatePlatformRequest = {
          name: formName.trim(),
          notes: formNotes.trim() || undefined,
          enable_on_design: formDesign,
          enable_on_execution: formExecution,
        }
        await updateMutation.mutateAsync({ id: editingPlatform.id, data: req })
        toast.success(`Platform "${formName}" updated`)
      }
      setDialogOpen(false)
    } catch (err) {
      logger.error('Platform save failed', { error: err })
      toast.error('Failed to save platform')
    }
  }

  const handleDeleteClick = (id: number, name: string) => {
    setPlatformToDelete({ id, name })
    setDeleteConfirmOpen(true)
  }

  // openapi: DELETE /api/platforms/{platform_id} → 204
  const handleDeleteConfirm = async () => {
    if (!platformToDelete) return
    try {
      await deleteMutation.mutateAsync(platformToDelete.id)
      toast.success(`"${platformToDelete.name}" deleted`)
    } catch (err) {
      logger.error('Delete platform failed', { error: err })
      toast.error('Failed to delete platform')
    } finally {
      setDeleteConfirmOpen(false)
      setPlatformToDelete(null)
    }
  }

  if (error) {
    return <Card><CardContent className="pt-6"><p className="text-destructive">{error instanceof Error ? error.message : 'Failed to load platforms'}</p></CardContent></Card>
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Platforms</CardTitle>
              <CardDescription>{filteredPlatforms.length} platform{filteredPlatforms.length !== 1 ? 's' : ''}</CardDescription>
            </div>
            <Button onClick={handleCreate}><Plus className="mr-2 h-4 w-4" /> New Platform</Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-4">
            <Select value={filterProjectId} onValueChange={setFilterProjectId}>
              <SelectTrigger className="w-[200px]"><SelectValue placeholder="All Projects" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects?.map((p) => (
                  <SelectItem key={p.id} value={p.id.toString()}>{p.prefix ? `[${p.prefix}] ` : ''}{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input placeholder="Search platforms..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="max-w-sm" />
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : filteredPlatforms.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">No platforms found.</div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Design</TableHead>
                    <TableHead>Execution</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPlatforms.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Monitor className="h-4 w-4 text-muted-foreground shrink-0" />
                          {p.name}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{p.project_name || `Project #${p.test_project_id}`}</TableCell>
                      <TableCell>
                        <Badge variant={p.enable_on_design ? 'success' : 'default'}>
                          {p.enable_on_design ? 'Yes' : 'No'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={p.enable_on_execution ? 'success' : 'default'}>
                          {p.enable_on_execution ? 'Yes' : 'No'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={p.is_open ? 'success' : 'default'}>
                          {p.is_open ? 'Open' : 'Closed'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDate(p.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(p)}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteClick(p.id, p.name)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialogMode === 'create' ? 'Create Platform' : 'Edit Platform'}</DialogTitle>
            <DialogDescription>{dialogMode === 'create' ? 'Add a platform for test execution targeting.' : 'Update platform details.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="e.g., Windows 11, Chrome 120, ARM64" />
            </div>
            {dialogMode === 'create' && (
              <div className="space-y-2">
                <Label>Project</Label>
                <Select value={formProjectId} onValueChange={setFormProjectId}>
                  <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>
                    {projects?.map((p) => (
                      <SelectItem key={p.id} value={p.id.toString()}>{p.prefix ? `[${p.prefix}] ` : ''}{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={formNotes} onChange={(e) => setFormNotes(e.target.value)} rows={3} />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Checkbox id="plat-design" checked={formDesign} onCheckedChange={(c) => setFormDesign(!!c)} />
                <Label htmlFor="plat-design">Enable on Design</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="plat-exec" checked={formExecution} onCheckedChange={(c) => setFormExecution(!!c)} />
                <Label htmlFor="plat-exec">Enable on Execution</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending}>
              {(createMutation.isPending || updateMutation.isPending) ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : dialogMode === 'create' ? 'Create' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete Platform"
        description={`Are you sure you want to delete "${platformToDelete?.name}"?`}
        onConfirm={handleDeleteConfirm}
        loading={deleteMutation.isPending}
        variant="destructive"
      />
    </div>
  )
}
